import { useEffect, useMemo, useRef, useState } from "react";
import { formatCollectionDisplayTitle } from "../lib/format";
import type { Collection } from "../types";
import { SearchBar } from "./SearchBar";

type Props = {
  open: boolean;
  onClose: () => void;
  collections: Collection[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  titlePrefix?: string;
};

export function CollectionPickerModal({
  open,
  onClose,
  collections,
  selectedId,
  onSelect,
  titlePrefix = "",
}: Props) {
  const [query, setQuery] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);

    const t = window.setTimeout(() => {
      const input = panelRef.current?.querySelector<HTMLInputElement>(
        ".poca-searchbar__input"
      );
      input?.focus();
    }, 0);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKeyDown);
      window.clearTimeout(t);
    };
  }, [open, onClose]);

  const items = useMemo(
    () =>
      collections.map((c) => ({
        collection: c,
        label: formatCollectionDisplayTitle(c.title, titlePrefix),
      })),
    [collections, titlePrefix]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(({ label }) => label.toLowerCase().includes(q));
  }, [items, query]);

  if (!open) return null;

  const handlePick = (id: string) => {
    onSelect(id);
    onClose();
  };

  return (
    <div
      className="poca-collection-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="poca-collection-modal-title"
    >
      <button
        type="button"
        className="poca-collection-modal__backdrop"
        onClick={onClose}
        aria-label="Close"
        tabIndex={-1}
      />
      <div className="poca-collection-modal__panel" ref={panelRef}>
        <header className="poca-collection-modal__header">
          <h2 id="poca-collection-modal-title" className="poca-collection-modal__title">
            Search Group
          </h2>
          <button
            type="button"
            className="poca-collection-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="poca-collection-modal__search">
          <SearchBar
            value={query}
            onChange={setQuery}
            placeholder="Search your favorite group"
            ariaLabel="Search your favorite group"
          />
        </div>

        <div className="poca-collection-modal__body">
          {filtered.length === 0 ? (
            <p className="poca-collection-modal__empty">No groups match your search.</p>
          ) : (
            <div className="poca-collection-modal__grid" role="list">
              {filtered.map(({ collection: c, label }) => {
                const active = c.id === selectedId;
                return (
                  <button
                    key={c.id}
                    type="button"
                    role="listitem"
                    className={`poca-collection${active ? " is-active" : ""}`}
                    onClick={() => handlePick(c.id)}
                    aria-pressed={active}
                    title={label}
                  >
                    <span className="poca-collection__circle">
                      {c.image?.url ? (
                        <img
                          src={c.image.url}
                          alt={c.image.altText ?? label}
                          loading="lazy"
                        />
                      ) : (
                        <span className="poca-collection__initial" aria-hidden="true">
                          {label.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </span>
                    <span className="poca-collection__title">{label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
